import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { importFreshBudget } from 'loot-core/client/actions';
import {
  CategoryDefinitions,
  type GuidedOnboardingPayload,
  type IGroupDefinition,
  OnboardingGroupType,
  OnboardingStep,
  OnboardingStepDefinition,
} from 'loot-core/shared/onboarding';

import { OnboardingContext } from '../../../../context/onboarding/OnboardingContext';
import { useNavigate } from '../../../../hooks/useNavigate';
import { AsyncStorageKey, useStorage } from '../../../../hooks/useStorage';
import { theme } from '../../../../style';
import { DISCORD_LINK } from '../../../../util/links';
import { Button, ButtonWithLoading } from '../../../common/Button';
import { Paragraph } from '../../../common/Paragraph';
import { Text } from '../../../common/Text';
import { View } from '../../../common/View';

import { OnboardingCheckbox } from './OnboardingCheckbox';
import { OnboardingMultipleInput } from './OnboardingMultipleInput';
import { SkipGuidedOnboarding } from './skip/SkipGuidedOnboarding';

type OnboardingStepsProps = {};

export function OnboardingSteps({}: OnboardingStepsProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const containerRef: React.RefObject<HTMLDivElement> = useRef();
  const userData = useSelector(state => state.user.data);
  const { getItem: getOnboardingData, setItem: setOnboardingData } = useStorage(
    AsyncStorageKey.ONBOARDING_DATA,
  );
  const [step, setStep] = useState<OnboardingStep>(OnboardingStep.Income);
  const [submitting, setSubmitting] = useState(false);
  const onboardingSteps = useMemo(
    () =>
      Object.values(OnboardingStep).filter(value => typeof value === 'number'),
    [],
  );
  const [isConfirmScreen, setIsConfirmScreen] = useState(false);

  const groupDefinitions = useMemo(
    () => CategoryDefinitions.filter(catDef => catDef.onboarding.step === step),
    [step],
  );

  useEffect(() => {
    if (!userData?.email) {
      navigate('/signup');
      return;
    }
  }, [userData?.email])

  useEffect(() => {
    (async () => {
      const onboardingData: { currentStep: OnboardingStep } =
        (await getOnboardingData()) || {};

      onboardingData?.currentStep && setStep(onboardingData?.currentStep);
    })();
  }, []);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [step]);

  let screen;
  if (isConfirmScreen) {
    screen = (
      <>
        <h2>You are ready to go!</h2>
        <Paragraph>
          Once we’ve finished setting up your planning view, we’ll guide you
          through Good Steward to help you get planning. If you’re the type who
          likes to explore on your own—you can do that too!
        </Paragraph>
        <Paragraph>
          Questions or feedback? Join our{' '}
          <a href={DISCORD_LINK} target="_blank" rel="noreferrer">
            discord
          </a>{' '}
          and help make Good Steward the best place to manage your money!
        </Paragraph>
        <ButtonWithLoading type="primary" loading={submitting} onClick={() => goToNextStep()}>
          Start Planning!
        </ButtonWithLoading>
      </>
    );
  } else {
    screen = (
      <>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 5,
            minHeight: 30,
            borderBottomWidth: 1,
            borderBottomColor: '#c7c7c7',
            paddingBottom: 10,
            marginBottom: 10,
            textAlign: 'center',
            fontWeight: 'bold',
            fontSize: 16,
          }}
        >
          <img
            width={30}
            src={OnboardingStepDefinition[step]?.icon}
            alt="heading-icon"
          />
          {OnboardingStepDefinition[step]?.heading}
        </View>
        <View
          style={{
            minHeight: 5,
            flexDirection: 'row',
            alignSelf: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '5px 0 20px',
            borderRadius: 50,
            gap: 1,
            overflow: 'hidden',
          }}
        >
          {onboardingSteps.map((group, index) => {
            return (
              <View
                key={group}
                style={{
                  width: 20,
                  backgroundColor:
                    index <= step
                      ? theme.buttonPrimaryBackground
                      : theme.mobileHeaderTextSubdued,
                  height: 5,
                }}
              />
            );
          })}
        </View>
        <Text style={{ fontWeight: 500, marginBottom: 20 }}>
          {OnboardingStepDefinition[step]?.description}
        </Text>
        <form
          onSubmit={e => {
            e.preventDefault();
            if (step === onboardingSteps.length - 1) {
              setIsConfirmScreen(true);
            } else {
              goToNextStep();
            }
          }}
        >
          <View style={{ minHeight: 'auto' }}>
            {groupDefinitions.map((catDef, index) => (
              <View key={catDef.name}>{getGroupDefinitionContent(catDef)}</View>
            ))}
          </View>

          <View
            style={{
              flexDirection: 'row',
              gap: 5,
              minHeight: 'auto',
            }}
          >
            {step >= 0 ? (
              <Button
                type="bare"
                style={{ flex: 1, minWidth: '49.5%' }}
                isSubmit={false}
                disabled={submitting}
                onClick={e => {
                  if (step === OnboardingStep.Income) {
                    navigate('/onboarding');
                  } else {
                    const prevStep = onboardingSteps.indexOf(step);
                    setStep(onboardingSteps[prevStep - 1] as OnboardingStep);
                  }
                }}
              >
                Go back
              </Button>
            ) : null}
            <ButtonWithLoading
              style={{ flex: 1 }}
              type="primary"
              isSubmit={true}
              loading={submitting}
            >
              Next
            </ButtonWithLoading>
          </View>
        </form>
        <SkipGuidedOnboarding />
      </>
    );
  }

  return (
    <OnboardingContext.Provider
      value={{
        step,
        setStep,
        goToNextStep,
        collectData,
        submitting,
        setSubmitting,
      }}
    >
      <View
        innerRef={containerRef}
        style={{
          padding: 15,
          borderRadius: 5,
          backgroundColor: 'white',
          overflowY: 'auto',
          maxWidth: 500,
        }}
      >
        {screen}
      </View>
    </OnboardingContext.Provider>
  );

  async function collectData(name, categories) {
    const data = await getOnboardingData();
    const onboardingData: GuidedOnboardingPayload =
      data && Object.keys(data).length
        ? data
        : {
          groups: [],
        };

    const groupIndex = onboardingData.groups?.findIndex(g => g.name === name);

    if (groupIndex === -1) {
      onboardingData.groups.push({
        name,
        categories,
      });
    } else {
      onboardingData.groups[groupIndex] = { name, categories };
    }
    await setOnboardingData({
      ...onboardingData,
      currentStep: step,
    });
  }

  function getGroupDefinitionContent(groupDefinition: IGroupDefinition) {
    switch (groupDefinition.onboarding.groupType) {
      case OnboardingGroupType.MultiInputs:
        return <OnboardingMultipleInput groupDefinition={groupDefinition} />;
      case OnboardingGroupType.Checkboxes:
        return <OnboardingCheckbox groupDefinition={groupDefinition} />;
    }
  }

  async function goToNextStep() {
    const lastStep = onboardingSteps[onboardingSteps.length - 1];
    const onboardingData: GuidedOnboardingPayload =
      (await getOnboardingData()) || {};

    if (step === lastStep) {
      await setOnboardingData({});
      setSubmitting(true);
      await dispatch(
        importFreshBudget({
          isEntirelySkipped: false,
          groups: onboardingData?.groups || [],
        }),
      );
    } else {
      const nextStep = onboardingSteps.indexOf(step) + 1;

      setStep(onboardingSteps[nextStep] as OnboardingStep);
    }
  }
}
